import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import CookieConsent from 'react-cookie-consent';

import config from '../../../../config.js';

import { getLoggedUser } from '../../../User/UserActions.js';
import { USER_GROUP_DEFAULT, getIsUserGroupFromParent, getUserGroup, getUserGroupOption } from '../../../User/UserGroupActions.js';
import { getConfig } from '../../../Config/ConfigActions.js';

import Icon from '../../../../components/Content/Icon.js';

import CmsPanel from '../../../Cms/components/content/CmsPanel.js';

// Import Style
import { getCountryFromLanguage, getEnabledLanguages } from '../../../Intl/IntlHelper.js';
import { PAYMENT_METHOD_LABELS } from '../../../Order/PaymentMethods.js';
import { CARRIER_LABELS } from '../../../Order/ShippingCarriers.js';

import Flag from '../../../../components/Flag/Flag.js';
import ExitIntent from '../view/ExitIntent.js';
import UserAdminLoginSwitcher from '../../../User/components/admin/UserAdminLoginSwitcher.js';
import VoucherPopin from '../../../Voucher/components/view/VoucherPopin.js';
import InstantRegister from '../../../User/components/auth/InstantRegister.js';
import NewsletterPopin from '../../../User/components/newsletter/NewsletterPopin.js';
import ParrainagePopin from '../../../User/components/view/ParrainagePopin.js';
import MultiOrderPopin from '../../../Order/components/view/MultiOrderPopin.js';
import AppLogo from '../view/AppLogo.js';

const POINTS = config.application.fidelity.pointsPerInvitation;

// Bust the immutable /assets cache when the payment/shipping tiles change
// (they keep the same filenames). Bump on each tile redesign.
const TILE_V = config.version || '2';

const renderBold = chunks => <b>{chunks}</b>;
const renderGold = chunks => <span className="text-warning">{chunks}</span>;

function Footer({ locale, user = null, userGroup = null, isUserGroupFromParent = false, isCookieConsentCustomActive = false }) {
    const [isMounted, mount] = useState(false);

    useEffect(() => {
        mount(true);
    }, []);

	const isIndividual = !userGroup || userGroup?.identifier === USER_GROUP_DEFAULT || isUserGroupFromParent;
	const isFidelityEligible = user && isIndividual && getUserGroupOption(userGroup, 'enableFidelity');

    return (
        <div className="footer-wrapper pb-5 pb-lg-2">
            <Container className="">

                {/* Réassurance */}
                {/* <Row className="pb-1 mb-2 align-items-center">
                    <Col lg="8" xl="9">
                        <CmsPanel slug="shipping-information" mode="compact" />
                    </Col>
                    <Col lg="4" xl="3" className="text-center text-lg-end">
                        <StarRating displayLink centered size="sm" />
                    </Col>
                </Row> */}

                {/* Programme de fidélité & parrainage */}
				{isFidelityEligible && !['admin', 'manager'].includes(user?.role) && (
					<div className="bg-danger bg-gradient rounded-4 p-3 mb-4">
						<Row className="align-items-center g-3">
							<Col md>
								<div className="text-uppercase small fw-bold text-white-50 mb-2">
									✨ <FormattedMessage id="footerLoyaltyEyebrow" defaultMessage="Loyalty & referral program" />
								</div>
								<div className="fw-bold fs-4 lh-sm text-white mb-2">
									<FormattedMessage
										id="footerLoyaltyTitle"
										defaultMessage="Refer a friend: <gold>{points} free points</gold> per referral"
										values={{ points: POINTS, gold: renderGold }}
									/>
								</div>
								<p className="mb-0 small text-white">
									<FormattedMessage
										id="footerLoyaltyText"
										defaultMessage="Your friend discover real good products, and you earn points redeemable for vouchers. The more you share the delicatessen, the more you save!"
										values={{ b: renderBold }}
									/>
								</p>
							</Col>
							<Col md="auto">
								<ParrainagePopin />
							</Col>
						</Row>
					</div>
				)}

                {/* Marque + moyens de paiement/livraison + liens CMS */}
                <Row className="g-0 g-md-2 mb-4 py-2">
                    <Col md="12" lg="3">
						<div  className="text-center text-lg-start">
							<div className="fw-bold fs-4 text-white mb-2">
								<AppLogo suffix="alt" className="w-50" />
							</div>
							<p className="small text-white-50">
								<CmsPanel slug="footer-baseline" isTemplate />
							</p>
							<div className="d-flex gap-3 justify-content-center justify-content-lg-start mb-3">
								{isMounted && <a href="https://www.facebook.com/directos.eu/" target="_blank" rel="noreferrer" className="text-white small text-decoration-underline">Facebook</a>}
								{isMounted && <a href="https://www.instagram.com/directos.eu/" target="_blank" rel="noreferrer" className="text-white small text-decoration-underline">Instagram</a>}
							</div>
							<Button as={Link} to={`/${locale}/page/contact`} variant="danger" id="contact-button" className="rounded-pill px-3 mb-3 w-100">
								<Icon icon="mail" /> <FormattedMessage id="pageContactUsLink" defaultMessage="Contact us" />
							</Button>
						</div>
                    </Col>
					<Col xs="6" md="4" lg="3">
						<div  className="mt-2 mt-md-0 pt-3 text-center">
							<b className="d-block text-white text-uppercase small mb-2">🔒 <FormattedMessage id="footerPaymentSecureInfo" defaultMessage="Secured payment guarantee" /></b>
							<div className="w-100 w-lg-75 m-auto d-flex flex-wrap align-items-center gap-2 justify-content-center">
								{Object.keys(PAYMENT_METHOD_LABELS).map(method => (
									<picture key={method}>
										<source srcSet={`${config.url}assets/images/payments/tiles/${method}.webp?v=${TILE_V}`} type="image/webp" />
										<img src={`${config.url}assets/images/payments/tiles/${method}.png?v=${TILE_V}`} alt={PAYMENT_METHOD_LABELS[method]} loading="lazy" style={{ height: 26, width: 'auto' }} />
									</picture>
								))}
							</div>
						</div>
					</Col>
                    <Col xs="6" md="4" lg="3">
						<div className="mt-2 mt-md-0 pt-3 text-center">
							<b className="d-block text-white text-uppercase small mb-2">🚚 <FormattedMessage id="footerShippingDeliveryInfo" defaultMessage="Delivery all over Europe" /></b>
							<div className="w-100 w-lg-75 m-auto d-flex flex-wrap align-items-center gap-2 justify-content-center">
								{Object.keys(CARRIER_LABELS).map(carrier => (
									<picture key={carrier}>
										<source srcSet={`${config.url}assets/images/shippings/tiles/${carrier}.webp?v=${TILE_V}`} type="image/webp" />
										<img src={`${config.url}assets/images/shippings/tiles/${carrier}.png?v=${TILE_V}`} alt={CARRIER_LABELS[carrier]} loading="lazy" style={{ height: 26, width: 'auto' }} />
									</picture>
								))}
							</div>
						</div>
                    </Col>
                    <Col md="4" lg="3">
						<div  className="mt-2 mt-md-0 pt-3 text-center text-lg-start">
							<b className="d-block text-white text-uppercase small mb-2"><Icon icon="info" /> <FormattedMessage id="footerAboutUsSection" defaultMessage="About us" /></b>
							<CmsPanel slug="footer-links" isTemplate locale={locale} />
						</div>
                    </Col>
                </Row>
            </Container>

            {/* Bas de page */}
            <div className="subfooter-wrapper px-2 text-center">
                <div>
                    <span className="h6"><FormattedMessage id="appLocalesAltTitle" defaultMessage="Also exists in" /></span> {getEnabledLanguages().map(language => <a key={language} href={`${config.url}${language}/`} className="d-inline-block me-1 px-1"><Flag country={getCountryFromLanguage(language)} /></a>)}
                </div>
                <small className="text-uppercase"><FormattedMessage id="appLegalFooterNotice" defaultMessage="For your health, eat at least five fruits and vegetables a day" /></small><br />
                <small>&copy; {new Date().getFullYear()} &middot; Directos.eu{isMounted && ` - v${config.version}`} - Made by <a href="https://offaxis.io" target="_blank" rel="noreferrer" className="text-reset text-decoration-none">OFF<span className="text-danger">/</span>AXIS</a></small>
            </div>

            <UserAdminLoginSwitcher />

            {(!userGroup || userGroup.identifier === USER_GROUP_DEFAULT) && <ExitIntent />}

            <NewsletterPopin />

            <VoucherPopin />

            {!user && <InstantRegister />}

            <MultiOrderPopin />

            {isCookieConsentCustomActive && (
                <CookieConsent
                    buttonText={<FormattedMessage id="close" defaultMessage="Close" />}
                    buttonClasses="btn btn-outline-primary btn-sm"
                    buttonStyle={{}}
                >
                    <small><FormattedMessage id="appCookieConsentText" defaultMessage="By continuing your visit to this site, you accept the use of cookies for the purposes of the functioning of the proposed service and statistics. No personal data will be communicated without your prior consent." /></small>
                    {' '}<Button size="outline-sm" variant="link" className="border-0" as={Link} to={`/${locale}/data-protection`}><FormattedMessage id="appCookieConsentPrivacyLink" defaultMessage="See our privacy policy" /></Button>
                </CookieConsent>
            )}

            {(!userGroup || !userGroup?.isFromParent) && (
                (!userGroup || (userGroup || {}).identifier === USER_GROUP_DEFAULT)
                ? <CmsPanel slug="footer-slider" isTemplate />
                : <CmsPanel slug={`footer-slider-${userGroup.identifier}`} />
            )}
        </div>
    );
}

function mapStateToProps(store, props) {
    return {
        locale: store.intl.locale,
        user: getLoggedUser(store),
		userGroup: getUserGroup(store),
		isUserGroupFromParent: getIsUserGroupFromParent(store),
        isCookieConsentCustomActive: getConfig(store, 'cookie-consent-custom'),
    };
}

Footer.propTypes = {
    locale: PropTypes.string.isRequired,
    user: PropTypes.object,
    userGroup: PropTypes.object,
	isUserGroupFromParent: PropTypes.bool,
    isCookieConsentCustomActive: PropTypes.string,
};

export default connect(mapStateToProps)(Footer);
